import { NotificationInboxStore } from './notification-inbox.store';
import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import type {
  AppNotification,
  DownloadNotification,
  ProgressNotification,
  ProgressStatus,
} from '@shared/models/notification.model';

const STORAGE_KEY = 'kh_notifications';
/** A finished job stays at the top of the panel this long, so its outcome is seen. */
const RECENTLY_FINISHED_MS = 10 * 60_000;

export function notificationScopeFromUrl(url: string): string {
  return url.match(/\/organizations\/([^/?#]+)/)?.[1] ?? 'account';
}

export function notificationText(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (
    typeof value === 'object' &&
    'message' in value &&
    typeof value.message === 'string'
  ) {
    return value.message;
  }
  return 'Ein unbekannter Fehler ist aufgetreten.';
}

interface NotificationState {
  items: AppNotification[];
  isOpen: boolean;
}

@Injectable({ providedIn: 'root' })
export class NotificationStore {
  private readonly router = inject(Router);
  readonly inbox = inject(NotificationInboxStore);
  private readonly activeScope = signal(
    notificationScopeFromUrl(this.router.url),
  );
  private readonly notificationScopes = new Map<string, string>();
  private readonly scopedNotifications = new Map<string, AppNotification[]>();
  private readonly _state = signal<NotificationState>({
    items: [],
    isOpen: false,
  });

  private readonly scopedItems = computed(() =>
    this._state().items.filter((item) => this.linkBelongsToScope(item)),
  );
  readonly items = computed(() => [...this.scopedItems()].reverse());
  readonly isOpen = computed(() => this._state().isOpen);
  readonly unreadCount = this.inbox.unreadCount;
  readonly activities = computed(() => {
    const server = this.inbox.activity();
    return [
      ...server,
      ...this.items().filter(
        (item) =>
          item.type === 'download' ||
          (item.type === 'progress' &&
            !server.some(
              (job) =>
                job.routerLink?.split('?')[0] ===
                item.routerLink?.split('?')[0],
            )),
      ),
    ];
  });

  /** What is happening right now: running jobs, downloads, and jobs that just finished. */
  readonly active = computed(() =>
    this.activities().filter((item) =>
      item.type === 'download'
        ? item.status !== 'expired'
        : item.type === 'progress' &&
          (item.status === 'in-progress' ||
            (!!item.finishedAt && Date.now() - item.finishedAt.getTime() < RECENTLY_FINISHED_MS)),
    ),
  );
  readonly runningCount = computed(
    () => this.activities().filter((item) => item.type === 'progress' && item.status === 'in-progress').length,
  );

  constructor() {
    // Notification titles, filenames and errors may identify a homeowner.
    // Keep them in memory; remove legacy persistent copies on upgrade.
    try {
      for (const key of Object.keys(localStorage)) {
        if (key === STORAGE_KEY || key.startsWith(`${STORAGE_KEY}:`))
          localStorage.removeItem(key);
      }
    } catch {
      /* Browser storage can be unavailable. */
    }
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        if (/^\/(login|logout|signup)([/?#]|$)/.test(event.urlAfterRedirects)) {
          this.scopedNotifications.clear();
          this.notificationScopes.clear();
          this.activeScope.set('account');
          this._state.set({ items: [], isOpen: false });
          return;
        }
        const requested = notificationScopeFromUrl(event.urlAfterRedirects);
        const scope = requested === 'account' ? this.activeScope() : requested;
        if (scope === this.activeScope()) return;
        this.activeScope.set(scope);
        this._state.update((state) => ({
          ...state,
          items: this.scopedNotifications.get(scope) ?? [],
        }));
      });
  }

  // ─── Sheet control ────────────────────────────────────────────────────────────

  openSheet(): void {
    this._state.update((s) => ({ ...s, isOpen: true }));
  }

  closeSheet(): void {
    this._state.update((s) => ({ ...s, isOpen: false }));
  }

  toggleSheet(): void {
    this._state.update((s) => ({ ...s, isOpen: !s.isOpen }));
  }

  // ─── Add notifications ────────────────────────────────────────────────────────

  addDownload(
    title: string,
    filename: string,
    downloadFn: () => Promise<Blob>,
  ): string {
    const id = this.newId();
    const scope = this.activeScope();
    this.notificationScopes.set(id, scope);
    const notification: DownloadNotification = {
      id,
      type: 'download',
      title,
      filename,
      status: 'preparing',
      timestamp: new Date(),
      read: false,
    };
    this.mutateItems((items) => [...items, notification], scope);

    downloadFn()
      .then((blob) => {
        if (!this.notificationScopes.has(id)) return;
        this.mutateItems(
          (items) =>
            items.map((item) =>
              item.id === id
                ? ({
                    ...item,
                    status: 'ready',
                    blob,
                    read: false,
                  } as DownloadNotification)
                : item,
            ),
          scope,
        );
      })
      .catch(() => {
        if (!this.notificationScopes.has(id)) return;
        this.mutateItems(
          (items) =>
            items.map((item) =>
              item.id === id
                ? ({
                    ...item,
                    status: 'failed',
                    read: false,
                  } as DownloadNotification)
                : item,
            ),
          scope,
        );
      });

    return id;
  }

  addProgress(title: string, routerLink?: string): string {
    const id = this.newId();
    const scope = routerLink?.includes('/organizations/')
      ? notificationScopeFromUrl(routerLink)
      : this.activeScope();
    this.notificationScopes.set(id, scope);
    const notification: ProgressNotification = {
      id,
      type: 'progress',
      title,
      routerLink,
      status: 'in-progress',
      timestamp: new Date(),
      read: false,
    };
    this.mutateItems((items) => [...items, notification], scope);
    return id;
  }

  updateProgress(
    id: string,
    patch: Partial<
      Pick<
        ProgressNotification,
        | 'status'
        | 'body'
        | 'progress'
        | 'errorCode'
        | 'errorMessage'
        | 'errorDetail'
      >
    >,
  ): void {
    if (!this.notificationScopes.has(id)) return;
    if ('body' in patch)
      patch = { ...patch, body: notificationText(patch.body) };
    if ('errorMessage' in patch)
      patch = { ...patch, errorMessage: notificationText(patch.errorMessage) };
    if ('errorDetail' in patch)
      patch = { ...patch, errorDetail: notificationText(patch.errorDetail) };
    const finished = patch.status && patch.status !== 'in-progress' ? { finishedAt: new Date() } : {};
    this.mutateItems(
      (items) =>
        items.map((item) =>
          item.id === id && item.type === 'progress'
            ? { ...item, ...patch, ...finished }
            : item,
        ),
      this.notificationScopes.get(id),
    );
  }

  // ─── Read management ──────────────────────────────────────────────────────────

  markRead(id: string): void {
    this.mutateItems((items) =>
      items.map((item) => (item.id === id ? { ...item, read: true } : item)),
    );
  }

  markAllRead(): void {
    this.mutateItems((items) => items.map((item) => ({ ...item, read: true })));
  }

  remove(id: string): void {
    this.mutateItems((items) => items.filter((item) => item.id !== id));
  }

  // ─── Private ──────────────────────────────────────────────────────────────────

  private mutateItems(
    updater: (items: AppNotification[]) => AppNotification[],
    scope = this.activeScope(),
  ): void {
    const items = updater(this.scopedNotifications.get(scope) ?? []).slice(
      -100,
    );
    this.scopedNotifications.set(scope, items);
    if (scope === this.activeScope()) {
      this._state.update((state) => ({ ...state, items }));
    }
  }

  private newId(): string {
    return `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  private linkBelongsToScope(item: AppNotification): boolean {
    if (!('routerLink' in item) || !item.routerLink) return true;
    if (!item.routerLink.includes('/organizations/')) return true;
    return notificationScopeFromUrl(item.routerLink) === this.activeScope();
  }
}
